import { camelize, camelizeKeys } from 'humps';
import memoize from 'lodash/memoize';
import { defineStore } from 'pinia';

import * as FacebookAPI from '@/apis/facebook';
import { getHttpErrorDescription, getHttpErrorStatus } from '@/apis/http-errors';
import {
  batchCreatePost,
  createEvent,
  createPost,
  deleteEvent,
  deletePost,
  fetchEvents,
  fetchPost,
  fetchPosts,
  getLinkedInMemberDetails,
  getRecommendedPublishTimes,
  http as schedulerHttp,
  reorderPost,
  searchLinkedInFollowers,
  updateEvent,
  updatePost,
} from '@/apis/scheduler';
import { ALL_PLATFORMS, defaultUtmEditorPanelState } from '@/app/scheduler/constants';
import { CHANNEL_FILTER_OPTIONS } from '@/app/scheduler/constants/schedulerFilters';
import { getPostKey, mapPostsByKey, mergeCase, parsePost } from '@/app/scheduler/utils';
import { unscheduledPosts } from '@/app/scheduler/utils/post-query';
import { CALENDAR_MODE } from '@/components/core/calendar/constants';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { browserStorageGetItem, browserStorageSetItem } from '@/utils/browserStorage';
import { formatISODate, isValidDate, parseISOOrElse } from '@/utils/dateUtils';
import { logger } from '@/utils/logger';
import { guessTimezone } from '@/utils/timezone';

import { useAuthStore } from './auth';

const BROWSER_STORAGE_KEYS = {
  CALENDAR_MODE: 'schedulerCalendarMode',
  CALENDAR_DATE: 'schedulerCalendarDate',
  DISMISSED_RECONNECTION_MESSAGES: 'dismissedReconnectionMessages',
};

const validatePlatform = (platform) => {
  if (!ALL_PLATFORMS.includes(platform)) {
    throw new Error(`${platform} is not a valid platform!`);
  }
};

const validateMode = (mode) => {
  if (!Object.values(CALENDAR_MODE).includes(mode)) {
    throw new Error(`"${mode}" is not a valid calendar mode!`);
  }
};

const validateDate = (date) => {
  if (!isValidDate(date)) {
    throw new Error(`${date} is not a valid date!`);
  }
};

const getPlatformBrandKey = (platform, brandId) => `${platform}-${brandId}`;

const filterPlatformsByFeatureFlags = (platforms) => {
  const flagStore = useFlagStore();

  const platformFeatureFlags = {
    youtube: 'youtube-shorts-publishing',
    snapchat: 'snapchat-publishing',
  };

  return platforms.filter((platform) => {
    const flagName = platformFeatureFlags[platform];
    if (!flagName) {
      return true;
    }

    return flagStore.ready && flagStore.flags[flagName];
  });
};

const getPlatformBrandIdsMap = (platforms, brandIds) => {
  const { identityBrandsById, canAccessSchedulerPlatform } = useIdentityStore();

  return platforms.reduce((map, platform) => {
    const accessibleBrandIds = brandIds.filter((brandId) => {
      const brand = identityBrandsById[brandId];
      return brand && canAccessSchedulerPlatform(platform, brand);
    });

    if (accessibleBrandIds.length > 0) {
      map.set(platform, accessibleBrandIds);
    }
    return map;
  }, new Map());
};

export const useSchedulerStore = defineStore('scheduler', {
  state: () => ({
    facebookSchedulingTokens: {},
    calendarMode: browserStorageGetItem(BROWSER_STORAGE_KEYS.CALENDAR_MODE) ?? CALENDAR_MODE.WEEK,
    calendarDate: parseISOOrElse(
      browserStorageGetItem(BROWSER_STORAGE_KEYS.CALENDAR_DATE),
      new Date(),
    ),
    shownReconnectionMessages: [],
    dismissedReconnectionMessages: browserStorageGetItem(
      BROWSER_STORAGE_KEYS.DISMISSED_RECONNECTION_MESSAGES,
    )
      ? browserStorageGetItem(BROWSER_STORAGE_KEYS.DISMISSED_RECONNECTION_MESSAGES).split(',')
      : [],
    postsByKey: {},
    nextPageURLsByQuery: {},
    loadedQueries: [],
    successfulQueries: [],
    lastQueryAt: null,
    postSaveTriggered: false,
    duplicationPostId: null,
    platformsWithErrors: [],
    pendingRequests: [],
    pendingPostActions: 0,
    pendingEventActions: 0,
    eventsByBrandId: {},
    selectedPosts: [],
    instagramLocationSearchList: [],
    facebookLocationSearchList: [],
    facebookAdGeolocations: {},
    errors: {
      facebookAdGeolocations: null,
    },
    pending: {
      fetchPost: false,
      instagramLocationSearchList: 0,
      facebookLocationSearchList: 0,
      facebookAdGeolocations: 0,
    },
    instagramTabContext: '',
    carouselPage: {},
    carouselActiveMedia: {},
    /** @type {import('../app/scheduler/components/EditPost/TikTokTrendingSounds/types').MusicInfo | null} */
    trendingSoundMusicInfo: null,
    // For use in Scheduler popup editors
    autoPublish: false,
    autoPublishChannelMap: {},
    activeSubScreen: null,
    popupType: null,
    popupData: null,
    pendingPostDict: {},
    recommendedPublishTimes: {},
    stalePostDataTime: 10 * 60 * 1000,
    postEditorData: {
      showSidePanel: false,
      showUtmPanel: defaultUtmEditorPanelState(),
      selectedChannels: [],
      parentChannel: {},
      postBrand: null,
      activeChannel: null,
    },
  }),
  getters: {
    currentPostBrand: (state) => state.postEditorData.postBrand,
    postList: (state) => Object.values(state.postsByKey),
    getPosts() {
      const { postList } = this;
      return memoize((query) => query.filterAndSort(postList));
    },
    getPost:
      (state) =>
      ({ platform, id }) =>
        state.postsByKey[getPostKey({ platform, id })],
    hasNextPage: (state) => (query) => !!state.nextPageURLsByQuery[query],
    isLoaded: (state) => (query) =>
      state.loadedQueries.some((loadedQuery) => loadedQuery.includes(query)),
    wasSuccessful: (state) => (query) =>
      state.successfulQueries.some((successfulQuery) => successfulQuery.includes(query)),
    getPendingRequest: (state) => (query) =>
      state.pendingRequests.find((pendingRequest) => pendingRequest.query.includes(query))?.request,
    isPending: (state) => (query) =>
      state.pendingRequests.some((pendingRequest) => pendingRequest.query.includes(query)),
    postActionPending: (state) => state.pendingPostActions > 0,
    eventActionPending: (state) => state.pendingEventActions > 0,
    isReconnectionMessageDismissed: (state) => (platform, brandId) =>
      state.dismissedReconnectionMessages.includes(getPlatformBrandKey(platform, brandId)),
    isReconnectionMessageShown: (state) => (platform, brandId) =>
      state.shownReconnectionMessages.includes(getPlatformBrandKey(platform, brandId)),
    getEvents:
      (state) =>
      ({ brandId }) =>
        state.eventsByBrandId[brandId] ?? [],
    currentFacebookSchedulingToken: (state) => {
      const authStore = useAuthStore();
      const brandId = state.postEditorData.postBrand?.id ?? authStore.currentBrand?.id;
      return state.facebookSchedulingTokens?.[brandId] ?? null;
    },
    getSelectedPosts: (state) => state.selectedPosts,
    isCalendarPostDataStale: (state) =>
      state.lastQueryAt && Date.now() - state.lastQueryAt > state.stalePostDataTime,
  },
  actions: {
    async getFacebookSchedulingToken({ brandId, ignoreCache = false } = {}) {
      const authStore = useAuthStore();
      const brandIdToUse = brandId ?? authStore.currentBrand?.id;

      // Try to find the token in our cache
      if (!ignoreCache && brandIdToUse in this.facebookSchedulingTokens) {
        return this.facebookSchedulingTokens[brandIdToUse];
      }
      try {
        const response = await FacebookAPI.default.getAccessToken({
          brandId: brandIdToUse,
          requiredScopes: ['pages_manage_posts', 'pages_read_engagement'],
        });
        const token = response?.data;

        // Cache the response so we're not hitting the server on every scheduler popup load, etc.
        this.facebookSchedulingTokens = {
          ...this.facebookSchedulingTokens,
          [brandIdToUse]: token,
        };

        return token;
      } catch (error) {
        const status = getHttpErrorStatus(error);
        if (status === 401 || status === 404) {
          logger.error(
            `schedulerStore getFacebookSchedulingToken request returned error: ${getHttpErrorDescription(error)}`,
            {},
            error,
          );
          return null;
        }
        logger.error('[GetFacebookSchedulingToken] returned error', {}, error);
        throw error;
      }
    },
    async getFacebookPageSearchList({ ids, q, limit }) {
      const response = await FacebookAPI.getPages({ ids, q, limit });
      const payload = response?.data;

      return payload;
    },

    async getLinkedInFollowerSearchList({ brandId, keyword }) {
      const response = await searchLinkedInFollowers({ brandId, keyword });
      return response?.data;
    },

    async getLinkedInMemberDetails({ brandId, personUrns }) {
      const response = await getLinkedInMemberDetails({ brandId, personUrns });
      return response?.data;
    },

    async fetchEvents({ brandId }) {
      const response = await fetchEvents({ brandId });
      const events = response.data.map(mergeCase);
      this.eventsByBrandId = {
        ...this.eventsByBrandId,
        [brandId]: events,
      };
    },
    async createEvent({ brandId, title, startDate, endDate, description, color }) {
      this.pendingEventActions += 1;
      try {
        return await createEvent({ brandId, title, startDate, endDate, description, color });
      } finally {
        this.pendingEventActions -= 1;
      }
    },
    async updateEvent({ id, brandId, title, startDate, endDate, description, color }) {
      this.pendingEventActions += 1;
      try {
        return await updateEvent({ id, brandId, title, startDate, endDate, description, color });
      } finally {
        this.pendingEventActions -= 1;
      }
    },
    async deleteEvent({ id }) {
      this.pendingEventActions += 1;
      try {
        await deleteEvent({ id });
      } finally {
        this.pendingEventActions -= 1;
      }
    },
    setReconnectionMessageDismissed(platform, brandId) {
      if (!this.isReconnectionMessageDismissed(platform, brandId)) {
        this.dismissedReconnectionMessages.push(getPlatformBrandKey(platform, brandId));
      }
      browserStorageSetItem(
        BROWSER_STORAGE_KEYS.DISMISSED_RECONNECTION_MESSAGES,
        this.dismissedReconnectionMessages.join(','),
      );
    },
    clearReconnectionMessageDismissed(platform, brandId) {
      this.dismissedReconnectionMessages = this.dismissedReconnectionMessages.filter(
        (key) => key !== getPlatformBrandKey(platform, brandId),
      );
      browserStorageSetItem(
        BROWSER_STORAGE_KEYS.DISMISSED_RECONNECTION_MESSAGES,
        this.dismissedReconnectionMessages.join(','),
      );
    },
    setReconnectionMessageShown(platform, brandId) {
      if (!this.isReconnectionMessageShown(platform, brandId)) {
        this.shownReconnectionMessages.push(getPlatformBrandKey(platform, brandId));
      }
    },
    setCalendarMode(newMode) {
      validateMode(newMode);
      browserStorageSetItem(BROWSER_STORAGE_KEYS.CALENDAR_MODE, newMode);
      this.calendarMode = newMode;
    },
    setCalendarDate(newDate) {
      validateDate(newDate);
      browserStorageSetItem(BROWSER_STORAGE_KEYS.CALENDAR_DATE, formatISODate(newDate));
      this.calendarDate = newDate;
    },
    async fetchPosts(query, { urls, limit } = {}) {
      if (!query.brandId && !query.brandIds)
        throw new Error('A brand must be specified to fetch posts!');
      if (this.isCalendarPostDataStale) {
        // Clear out stale data after 10 minutes
        this.successfulQueries = [];
        this.loadedQueries = [];
        this.lastQueryAt = null;
      }
      if (!urls && (this.wasSuccessful(query) || this.isPending(query))) {
        await this.getPendingRequest(query);
        return;
      }

      const brandIds = query.brandIds || [query.brandId];

      const platformBrandMap = urls
        ? null
        : getPlatformBrandIdsMap(filterPlatformsByFeatureFlags(query.platforms), brandIds);

      const params = {
        sort: query.sort,
        status: query.statuses,
        hasScheduledTime: query.hasScheduledTime,
        ...(query.interval && {
          start: formatISODate(query.interval.start),
          end: formatISODate(query.interval.end),
        }),
        limit,
      };

      const request = Promise.allSettled(
        urls
          ? urls.map(async ({ url, platform }) => ({
              platform,
              ...(await schedulerHttp.get(url)),
            }))
          : [...platformBrandMap.entries()].map(async ([platform, platformBrandIds]) => ({
              platform,
              ...(await fetchPosts({
                ...params,
                platform,
                brandIds: platformBrandIds.join(','),
              })),
            })),
      );

      if (!urls) this.pendingRequests.push({ query, request });

      let platformsWithErrors = [...(platformBrandMap?.keys() || query.platforms)];

      try {
        const responses = await request;

        const successfulPayloads = responses
          .filter(({ value }) => value)
          .map(({ value: { platform, data } }) => ({ platform, ...data }));

        const posts = successfulPayloads.flatMap(({ platform, data }) =>
          data.map(parsePost(platform)),
        );
        const nextPageURLs = successfulPayloads
          .map(({ platform, paging: { next } }) => ({ platform, url: next }))
          .filter(({ url }) => url);

        this.postsByKey = { ...this.postsByKey, ...mapPostsByKey(posts) };
        this.nextPageURLsByQuery = {
          ...this.nextPageURLsByQuery,
          [query]: nextPageURLs.length && nextPageURLs,
        };

        if (successfulPayloads.length === responses.length && !this.wasSuccessful(query)) {
          this.lastQueryAt = Date.now();
          this.successfulQueries.push(query);
        }

        const successfulPlatforms = successfulPayloads.map(({ platform }) => platform);
        platformsWithErrors = platformsWithErrors.filter(
          (platform) => !successfulPlatforms.includes(platform),
        );

        const errors = responses.map(({ reason }) => reason).filter((reason) => reason);
        if (errors.length > 0) {
          throw new Error(errors);
        }
      } finally {
        this.pendingRequests = this.pendingRequests.filter(
          (pendingRequest) => pendingRequest.query !== query,
        );
        if (!this.isLoaded(query)) this.loadedQueries.push(query);
        this.platformsWithErrors = [
          ...new Set([...this.platformsWithErrors, ...platformsWithErrors]),
        ];
      }
    },
    async fetchNextPage(query) {
      const urls = this.nextPageURLsByQuery[query];
      if (urls) {
        await this.fetchPosts(query, { urls });
      }
    },
    replacePost(post) {
      const { platform } = post;
      validatePlatform(platform);
      this.postsByKey = {
        ...this.postsByKey,
        [getPostKey(post)]: Object.freeze(parsePost(platform)(post)),
      };
    },
    removePost({ platform, id }) {
      validatePlatform(platform);
      const postKey = getPostKey({ platform, id });
      const newValue = {
        ...this.postsByKey,
      };
      delete newValue[postKey];
      this.postsByKey = newValue;
    },
    async fetchPost({ platform, id }) {
      this.pending.fetchPost = true;
      validatePlatform(platform);
      const response = await fetchPost({ platform, id });
      const post = parsePost(platform)(response.data);
      this.postsByKey = {
        ...this.postsByKey,
        [getPostKey({ platform, id })]: Object.freeze(post),
      };
      this.pending.fetchPost = false;
    },
    async createPost({ platform, approvalRequests, ...data }) {
      validatePlatform(platform);
      this.pendingPostActions += 1;
      data.approvalRequests = approvalRequests?.map((request) => ({
        requestedByUserId: request.requestedByUserId,
        reviewUserId: request.reviewUserId,
      }));
      try {
        const response = await createPost({ platform, data });

        const post = parsePost(platform)(response.data);
        this.duplicationPostId = post.id;
        this.postsByKey = {
          ...this.postsByKey,
          [getPostKey(post)]: Object.freeze(post),
        };
        this.postSaveTriggered = true;
        return post;
      } finally {
        this.pendingPostActions -= 1;
      }
    },
    async batchCreatePost({ platform, duplicateToBrandIds, approvalRequests, ...data }) {
      validatePlatform(platform);
      this.pendingPostActions += 1;
      data.approvalRequests = approvalRequests?.map((request) => ({
        requestedByUserId: request.requestedByUserId,
        reviewUserId: request.reviewUserId,
      }));

      if (duplicateToBrandIds && duplicateToBrandIds.length > 0) {
        data.duplicateToBrandIds = duplicateToBrandIds;
      }

      try {
        const response = await batchCreatePost({ platform, data });
        const postsArray = response.data;
        postsArray.forEach((post) => {
          const parsedPost = parsePost(platform)(post);
          this.postsByKey = {
            ...this.postsByKey,
            [getPostKey(parsedPost)]: Object.freeze(parsedPost),
          };
        });

        this.postSaveTriggered = true;
        return postsArray;
      } finally {
        this.pendingPostActions -= 1;
      }
    },
    async updatePost({ platform, id, approvalRequests, ...data }) {
      validatePlatform(platform);
      this.pendingPostActions += 1;
      data.approvalRequests = approvalRequests?.map((request) => ({
        requestedByUserId: request.requestedByUserId,
        reviewUserId: request.reviewUserId,
      }));
      try {
        const response = await updatePost({ platform, id, data });
        const post = parsePost(platform)(response.data);
        this.postsByKey = {
          ...this.postsByKey,
          [getPostKey(post)]: Object.freeze(post),
        };
        this.postSaveTriggered = true;
      } finally {
        this.pendingPostActions -= 1;
      }
    },
    async deletePost({ platform, id }) {
      validatePlatform(platform);
      this.pendingPostActions += 1;
      try {
        await deletePost({ platform, id });
        const postKey = getPostKey({ platform, id });
        const newValue = {
          ...this.postsByKey,
        };
        delete newValue[postKey];
        this.postsByKey = newValue;
      } finally {
        this.pendingPostActions -= 1;
      }
    },
    async reorderPost({ platform, id }, after) {
      validatePlatform(platform);

      const post = this.getPost({ platform, id });
      const postKey = getPostKey(post);
      if (!post) {
        throw new Error('Post must be loaded before reordering!');
      }

      // Optimistically reorder posts
      let newSortIndex = post.sortIndex;
      const originalSortIndex = post.sortIndex;
      const posts = this.getPosts(unscheduledPosts.forPlatform(platform));
      if (after) {
        const afterPost = this.getPost({ platform, id: after });
        const rightNeighbor = posts.filter(({ sortIndex }) => sortIndex < afterPost.sortIndex)[0];
        if (rightNeighbor) {
          newSortIndex = (afterPost.sortIndex + rightNeighbor.sortIndex) / 2;
        } else {
          newSortIndex = afterPost.sortIndex - 1;
        }
      } else {
        newSortIndex = posts[0].sortIndex + 1;
      }

      this.postsByKey = {
        ...this.postsByKey,
        [postKey]: Object.freeze({ ...post, sortIndex: newSortIndex }),
      };

      this.pendingPostActions += 1;
      try {
        await reorderPost({ platform, id, brandId: post.brandId, after });
      } catch (e) {
        this.postsByKey = {
          ...this.postsByKey,
          [postKey]: Object.freeze({ ...post, sortIndex: originalSortIndex }),
        };
        throw e;
      } finally {
        this.pendingPostActions -= 1;
      }
    },
    async getInstagramLocationSearchList({ query, limit, brandId = null }) {
      const authStore = useAuthStore();
      const resolvedBrandId = brandId ?? authStore.currentBrand?.id;

      this.pending.instagramLocationSearchList += 1;
      try {
        const response = await FacebookAPI.getLocations({ brandId: resolvedBrandId, query, limit });
        const payload = response?.data;

        this.instagramLocationSearchList = payload;

        return payload;
      } finally {
        this.pending.instagramLocationSearchList -= 1;
      }
    },
    clearInstagramLocationSearchList() {
      this.instagramLocationSearchList = [];
    },
    async getFacebookLocationSearchList({ query, limit, brandId = null }) {
      const authStore = useAuthStore();
      const resolvedBrandId = brandId ?? authStore.currentBrand?.id;

      this.pending.facebookLocationSearchList += 1;
      try {
        const response = await FacebookAPI.getLocations({ brandId: resolvedBrandId, query, limit });
        const payload = response?.data;

        this.facebookLocationSearchList = payload;

        return payload;
      } finally {
        this.pending.facebookLocationSearchList -= 1;
      }
    },
    clearFacebookLocationSearchList() {
      this.facebookLocationSearchList = [];
    },
    async facebookAdGeolocationSearch(locationFields, brandId = null) {
      const authStore = useAuthStore();
      const resolvedBrandId = brandId ?? authStore.currentBrand?.id;

      this.pending.facebookAdGeolocations += 1;
      try {
        const promises = locationFields.map((locationField) =>
          FacebookAPI.getLocations({
            brandId: resolvedBrandId,
            locationType: 'adgeolocation',
            locationFields: locationField,
            limit: 1000,
          }),
        );
        const results = await Promise.all(promises);
        results.forEach((result) => {
          const type = result.data[0]?.type;
          this.facebookAdGeolocations = {
            ...this.facebookAdGeolocations,
            [camelize(type)]: camelizeKeys(result.data),
          };
        });
        this.errors.facebookAdGeolocations = null;
      } catch (error) {
        this.errors.facebookAdGeolocations = error;
      } finally {
        this.pending.facebookAdGeolocations -= 1;
      }
    },
    setInstagramTabContext(tabContext) {
      this.instagramTabContext = tabContext;
    },
    setCarouselPage({ carouselPage, activeMedia, postType }) {
      this.carouselPage[postType] = carouselPage;
      this.carouselActiveMedia = activeMedia;
    },
    resetCarouselPage() {
      this.carouselPage = {};
      this.carouselActiveMedia = {};
    },
    setAutoPublish(newAutoPublish) {
      this.autoPublish = newAutoPublish;
    },
    setChannelAutoPublish(autoPublishVal, brandId, editorType) {
      if (!this.autoPublishChannelMap[brandId]) {
        this.autoPublishChannelMap[brandId] = {};
      }
      this.autoPublishChannelMap[brandId][editorType] = autoPublishVal;
    },
    setActiveSubScreen(subScreen) {
      this.activeSubScreen = subScreen;
    },
    openPopup({ type, data = null }) {
      this.popupType = type;
      this.popupData = data;
    },
    closePopup() {
      this.popupType = null;
      this.popupData = null;
    },
    clearPendingPostDict() {
      this.pendingPostDict = {};
    },
    updatePendingPostStatus({ id, result }) {
      if (id && result) {
        this.pendingPostDict[id] = result;
      }
    },
    toggleSidePanel(post) {
      // Close UTM panels for all platforms when opening the approvals/comments panel
      if (!this.postEditorData.showSidePanel) {
        this.setPostEditorData({
          showUtmPanel: defaultUtmEditorPanelState(),
        });
      }

      this.setPostEditorData({ showSidePanel: !this.postEditorData.showSidePanel });
    },
    setUtmPanelState(postType, isOpen) {
      this.setPostEditorData({
        showUtmPanel: {
          ...this.postEditorData.showUtmPanel,
          [postType]: isOpen,
        },
        ...(isOpen && { showSidePanel: false }),
      });
    },
    async listRecommendedPublishTimes({ brandId, platform }) {
      const timezone = guessTimezone();
      const response = await getRecommendedPublishTimes({ brandId, platform, timezone });

      this.recommendedPublishTimes[brandId] = {
        ...this.recommendedPublishTimes[brandId],
        [platform]: response?.data?.data,
      };
      return this.recommendedPublishTimes[brandId][platform];
    },
    clearRecommendedPublishTimes() {
      this.recommendedPublishTimes = {};
    },
    getChannelFilterOptions(channels) {
      const flagStore = useFlagStore();
      const hasYoutubeShortsPublishingFlag =
        flagStore.ready && flagStore.flags.youtubeShortsPublishing;
      const hasSnapchatPublishingFlag = flagStore.ready && flagStore.flags.snapchatPublishing;
      const filteredChannels = channels.filter((channel) => {
        const isYoutube = channel === CHANNEL_FILTER_OPTIONS.YOUTUBE.value;
        const isSnapchat = channel === CHANNEL_FILTER_OPTIONS.SNAPCHAT.value;
        if (isYoutube) return hasYoutubeShortsPublishingFlag;
        if (isSnapchat) return hasSnapchatPublishingFlag;
        return true;
      });
      return filteredChannels;
    },
    addSelectedPost(post) {
      this.selectedPosts.push(post);
    },
    removeSelectedPost(post) {
      if (post?.id) {
        this.selectedPosts = this.selectedPosts.filter((p) => p.id !== post.id);
      }
    },
    clearSelectedPosts() {
      this.selectedPosts = [];
    },
    clearSaveTrigger() {
      this.postSaveTriggered = false;
    },
    setPostEditorData(newData) {
      this.postEditorData = {
        ...this.postEditorData,
        ...newData,
      };
    },
    addChannelToAutoPublishMap(brandId, postType) {
      if (!this.autoPublishChannelMap[brandId]) {
        this.autoPublishChannelMap[brandId] = {};
      }
      this.autoPublishChannelMap[brandId][postType] = false;
    },
    removeChannelFromAutoPublishMap(brandId, postType) {
      delete this.autoPublishChannelMap[brandId]?.[postType];
    },
  },
});
